import React from 'react';
import PropTypes from 'prop-types';

const pathOutletRE = new RegExp("data/port/.*/Output.*");
const pathSensorRE = new RegExp("data/port/.*/Sensor.*");
const pathInletRE = new RegExp("data/port/.*/Input.*");

import TableCell from '../DetailPageComponents/TableCell.jsx';
import PowerSwitchRow from '../DetailPageComponents/PowerSwitchRow.jsx';
import ChartCell from '../DetailPageComponents/ChartCell.jsx';
import StateCell from '../DetailPageComponents/StateCell.jsx';
import OutletGroupHeader from '../DetailPageComponents/OutletGroupHeader.js';
import ButtonCell from "../DetailPageComponents/ButtonCell.jsx";

import { mapUnit } from '../util/topic.js';

export const INLET = 'inlet';
export const OUTLET = 'outlet';
export const SENSOR = 'sensor';
export const INFO = 'info';
export const SWITCHED = 'switched';
export const BINARY_IN = 'bi';
export const BINARY_OUT = 'do';
export const GENERIC = 'generic';
export const RESIDUAL = 'residual';
export const BRANCH = 'branch';

const INLET_FIELD_ORDER = [
    'NAME',
    'PHASE',
    'Voltage',
    'VoltageLL',
    'Current',
    'UnbalancedCurrent',
    'PowerW',
    'PowerVA',
    'PowerVAR',
    'PowerFactor',
    'KWhTotal',
    'PhaseAngle',
    'LineFreq'
];

const addToPaths = function (dsPath, fieldName, pathList, unit) {
    if (dsPath && dsPath[fieldName] && dsPath[fieldName].v !== undefined) {
        if (pathList[fieldName] === undefined) {
            pathList[fieldName] = unit;
        }
        return true;
    }
    return false;
};

export function groupOutlets(outletNode) {
    // Let's check if there is a grouping of outlets. This would happen over a mapping to a parent
    let outletGroups = {};
    for (let portIndex in outletNode) {
        const outlet = outletNode[portIndex];
        if (outlet.parent) {
            let group = outletGroups[outlet.parent];
            if (!group) {
                group = [portIndex];
                outletGroups[outlet.parent] = group;
            } else {
                group.push(portIndex);
            }
        }
    }
    return outletGroups;
}


export class GenericPDUDriver extends React.Component {

    static contextTypes = {
        basePath: PropTypes.string.isRequired
    }

    getPortName (portNumber) {
        const topic = this.props.p.getPath("config/port/" + portNumber + "/OutletName");
        if (topic && topic.v) {
            return topic.v;
        }
        return "Port " + (+portNumber + 1);
    }

    static reportTopics() {
        return [
            "o/+",
            "o/+/Current",
            "o/+/KWhSubtotal",
            "o/+/KWhTotal",
            "i/+",
            "i/+/Current",
            "i/+/KWhSubtotal",
            "i/+/PowerW",
            "i/+/KWhTotal",
            "i/+/Voltage",
            "o/+",
            "o/+/val"
        ];
    }

    static reportFilter (t, p, dsSubPath) {
        return new Promise(
            function (resolve) {
                const outletPath = dsSubPath.o, inletPath = dsSubPath.i;
                const unitA = t("sensor_units.AMPERE"), unitKWh = t("sensor_units.KWh"), unitW = t("sensor_units.WATT");
                let plans = [], devicePlan = null, outletPlan = null;
                let outletPorts = {}, outletPaths = {}, inletPorts ={}, inletPaths = {};
                for (var portIdx in inletPath) {
                    if (!Object.prototype.hasOwnProperty.call(inletPath, portIdx)) {
                        continue;
                    }
                    let inletsFound = false;
                    if (inletPath[portIdx].name !== undefined) {
                        // Input exists, see if there is a current
                        inletsFound |= addToPaths(inletPath[portIdx], "Current", inletPaths, unitA);
                        inletsFound |= addToPaths(inletPath[portIdx], "KWhSubtotal", inletPaths, unitKWh);
                        inletsFound |= addToPaths(inletPath[portIdx], "PowerW", inletPaths, unitW);
                        inletsFound |= addToPaths(inletPath[portIdx], "KWhTotal", inletPaths, unitKWh);
                        inletsFound |= addToPaths(inletPath[portIdx], "Voltage", inletPaths, "V");

                        if (inletsFound) {
                            inletPorts[portIdx] = inletPath[portIdx].name;

                            // We know for sure that there is at least one input, see if the devicePlan exists already
                            if (!devicePlan) {
                                devicePlan = {
                                    b:"i/",
                                    n:inletPaths,
                                    f:inletPorts
                                };
                                plans.push({
                                    type:"device",
                                    name:t("reporting.device_report"),
                                    plan:devicePlan
                                });
                            }
                        }
                    }
                }
                for (portIdx in outletPath) {
                    if (!Object.prototype.hasOwnProperty.call(outletPath, portIdx)) {
                        continue;
                    }

                    let outletsFound = false;

                    if (outletPath[portIdx].name !== undefined) {
                        // Outlet exists, see if there is a current
                        outletsFound |= addToPaths(outletPath[portIdx], "Current", outletPaths, unitA);
                        outletsFound |= addToPaths(outletPath[portIdx], "KWhSubtotal", outletPaths, unitKWh);
                        outletsFound |= addToPaths(outletPath[portIdx], "KWhTotal", outletPaths, unitKWh);
                        if (outletsFound) {
                            outletPorts[portIdx] = outletPath[portIdx].name;
                            // We know for sure that there is at least one outlet, see if the outletPlan exists already
                            if (!outletPlan) {
                                outletPlan = {
                                    b:"o/",
                                    n:outletPaths,
                                    f:outletPorts
                                };
                                plans.push({
                                    type:"outlets",
                                    name:"",
                                    plan:outletPlan
                                });
                            }
                        }
                    }
                }
                resolve(plans);
            }
        );
    }

    static getPortTitle(dataPath, portNumber, basePath, t, p) {
        if (pathOutletRE.exec(dataPath)) {
            return getPortName(basePath + "config/port/" + portNumber + "/OutletName", portNumber, t, p);
        } else if (pathSensorRE.exec(dataPath)) {
            return getPortName(basePath + "config/port/" + portNumber + "/SensorName", portNumber, t, p);
        } else if (pathInletRE.exec(dataPath)) {
            return getPortName(basePath + "config/port/" + portNumber + "/InputName", portNumber, t, p);
        }
    }


    countPortsWithTopic(topic, rootTopic) {
        let count = 0;
        for (let idx in rootTopic) {
            if (Object.prototype.hasOwnProperty.call(rootTopic, idx) && Object.prototype.hasOwnProperty.call(rootTopic[idx], topic)) {
                count++;
            }
        }
        return count;
    }

    countPortsWithType(type, rootTopic) {
        let count = 0;
        for (let idx in rootTopic) {
            if (Object.prototype.hasOwnProperty.call(idx) && Object.prototype.hasOwnProperty.call(rootTopic[idx], "type") && rootTopic[idx].type === type) {
                count++;
            }
        }
        return count;
    }

    renderDigitalOutputs (doNode, baseNode, numDigitalOutputs, num, t) {
        if (!doNode) {
            return null;
        }

        var rows = [];
        for (const i in doNode) {
            if (Object.prototype.hasOwnProperty.call(doNode, i) && doNode[i].name !== undefined) {
                rows.push((
                    <tr key={"out-" + i}>
                        <td className="ta-right">{ this.formatOutletNumber(doNode, i)}</td>
                        { !this.props.showConfig && (<TableCell dataPath={"do/" + i} subAttr="name" readOnly={true} />)}
                        { this.props.showConfig && (<TableCell dataPath={"do/" + i} subAttr="name" readOnly={false} />)}
                        <td><PowerSwitchRow dataPath={"do/" + i + "/state"} readOnly={false} /></td>
                    </tr>
                ));
            }
        }

        if (!rows) {
            return null;
        }

        return (
            <div className="overview__details__item is_locked" id="digitaloutput">
                <h3>{ t('details_digital_outputs.header') }</h3>
                <table cellSpacing={0} cellPadding={0}>
                    <thead>
                        <tr>
                            <th className="ta-right">#</th>
                            <th>{ t('general.name') }</th>
                            { !this.props.showConfig && ( <th>{ t('general.state') }</th> )}
                        </tr>
                    </thead>
                    <tbody>
                        { rows }
                    </tbody>
                </table>
            </div>
        );
    }

    /// Overwrite this function in subclasses to render specific info lines without
    /// adding translation keys
    renderInfoLine() {
        return null;
    }

    renderInfo(statusNode) {
        const { t, p } = this.props;
        const self = this;
        let infoParentNode = statusNode ? statusNode.info : null;
        let infoNode = infoParentNode ? infoParentNode.v : null;
        if (!infoNode) {
            return null;
        }
        let rows = [];

        // Track which rows we have rendered so far
        let keysRendered = ["VendorAdminUrl"];

        // Render one row
        let renderRow = function(key) {
            if (keysRendered.indexOf(key) === -1 && Object.prototype.hasOwnProperty.call(infoNode, key)) {
                // Only render it if we can translate it
                const translationKey = 'topic_titles_info.' + key?.toLowerCase();
                const title = t(translationKey);
                if (title === translationKey) {
                    const renderedValue = self.renderInfoLine(key, infoNode[key]);
                    if (renderedValue === null) {
                        return null;
                    }
                }
                rows.push((
                    <tr key={"info-" + key}>
                        <td>{title}</td>
                        <td>{infoNode[key]}</td>
                    </tr>
                ));
            }
        };

        // Always render these special rows first
        renderRow('Model');
        renderRow('ProductId');
        renderRow('MacAddress');
        renderRow('FirmwareRevision');

        for (let key in infoNode) {
            if (key === 'Model' || key === 'ProductId' || key === 'MacAddress' || key === 'FirmwareRevision') {
                continue;
            }
            renderRow(key);
        }

        if (!rows) {
            return null;
        }

        return (
            <div className="overview__details__item is_locked" id="info">
                <h3>{ t('details_pdu_identification.header') }</h3>
                <table cellSpacing={0} cellPadding={0}>
                    <thead>
                        <tr>
                            <th>{ t('general.name') }</th>
                            <th>{ t('general.value') }</th>
                        </tr>
                    </thead>
                    <tbody>
                        { rows }
                    </tbody>
                </table>
            </div>
        );
    }

    renderDigitalInputs (diNode, baseNode, NumberDigitalInputs, num, t) {
        if (!diNode) {
            return null;
        }

        var rows = [];
        for (const i in diNode) {
            if (Object.prototype.hasOwnProperty.call(diNode, i) && diNode[i].name !== undefined) {
                rows.push((
                    <tr key={"in-" + i} id={"bi/" + i}>
                        <td className="ta-right">{ this.formatOutletNumber(diNode, i)}</td>
                        { !this.props.showConfig && (<TableCell dataPath={"bi/" + i} subAttr="name" readOnly={true} />)}
                        { this.props.showConfig && (<TableCell dataPath={"bi/" + i} subAttr="name" readOnly={false} />)}
                        { !this.props.showConfig && (<StateCell key={"power-" + i} dataPath={"bi/" + i + "/DigitalInputState"} />) }
                    </tr>
                ));
            }
        }

        if (!rows) {
            return null;
        }

        return (
            <div className="overview__details__item is_locked" id="digitalinputState">
                <h3>{ t('details_digital_inputs.header') }</h3>
                <table cellSpacing={0} cellPadding={0}>
                    <thead>
                        <tr>
                            <th className="ta-right">#</th>
                            <th>{ t('general.name') }</th>
                            { !this.props.showConfig && (<th>{ t('general.state') }</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        { rows }
                    </tbody>
                </table>
            </div>
        );
    }

    renderEnviron (sensorNode, baseNode, numSensors, num, t) {
        var rows = [];
        if (numSensors === 0) {
            return null;
        }

        for (let i in sensorNode) {
            const unit = t('sensor_units.' + sensorNode[i].unit);
            rows.push((
                <tr key={"environ-" + i}>
                    <td className="ta-right">{ this.formatOutletNumber(sensorNode, i)}</td>
                    { !this.props.showConfig && (<TableCell maxLength="32" size="16" dataPath={"s/" + i } subAttr="name" type="text" />)}
                    { this.props.showConfig && (<TableCell maxLength="32" size="16" dataPath={"s/" + i } subAttr="name" type="text" readOnly={false} />)}
                    <TableCell dataPath={"s/" + i } subAttr="type" translatePrefix={"topic_titles"} />
                    { !this.props.showConfig && (<TableCell dataRound={2}  dataPath={"s/" + i + "/val"} unit={unit}  popupName={t('topic_titles.sensorvalue')} chartTitle={sensorNode[i].name} />)}
                    { !this.props.showConfig && (<ChartCell chartPath={"s/" + i + "/val"} unit={unit}  popupName={t('topic_titles.sensorvalue')} chartTitle={sensorNode[i].name}/>)}
                    
                </tr>
            ));
        }
        return (
            <div className="overview__details__item" id="environ">
                <h3>{ t('details_environ_sensors.header') }</h3>
                <table cellSpacing={0} cellPadding={0}>
                    <thead>
                        <tr>
                            <th className="ta-right">#</th>
                            <th>{ t('general.name') }</th>
                            <th>{ t('general.type') }</th>
                            { !this.props.showConfig &&
                                <th className="ta-right">{ t('general.value') }</th>
                            }
                            { !this.props.showConfig &&
                                <th>{ t('details_environ_sensors.history_value') }</th>
                            }
                        </tr>
                    </thead>
                    <tbody>
                        { rows }
                    </tbody>
                </table>
            </div>
        );
    }

    renderSwitches (outletNode, baseNode, numSwitched) {
        const { t, p } = this.props;
        if (this.props.showConfig || numSwitched ===  0) {
            return null;
        }

        let rows = [];

        // First lets check if any row has an icon at all
        let hasIcon = false;
        for (let i in outletNode) {
            hasIcon = outletNode[i].type;
            if (hasIcon) {
                break;
            }
        }

        for (let i in outletNode) {
            if (outletNode[i].switch) {
                const icon = hasIcon ? (
                    <td className="ta-right">
                        <img className="type" src={"/img/types/" + (outletNode[i].type ? outletNode[i].type : "NONE") + ".svg"} />
                    </td>
                ) : null;
                rows.push((
                    <tr key={"switch-" + i}>
                        <td className="ta-right">{ this.formatOutletNumber(outletNode, i)}</td>
                        <TableCell dataPath={ "o/" + i  } subAttr="name" size="12" maxLength="32" readOnly={true} />
                        { icon }
                        <td><PowerSwitchRow key={"power-" + i} dataPath={"o/" + i + "/switch"} readOnly={false} /></td>
                    </tr>
                ));
            }
        }

        return (
            <div className="overview__details__item is_locked" id="switching">
                <h3>{ t('details_outlet_switches.header') }</h3>
                <table cellSpacing={0} cellPadding={0}>
                    <thead>
                        <tr>
                            <th className="ta-right">#</th>
                            <th>{ t('general.name') }</th>
                            { hasIcon && ( <th className="ta-right">{ t('details_outlet_switches.outlets') }</th> )}
                            <th>{ t('general.state') }</th>
                        </tr>
                    </thead>
                    <tbody>
                        { rows }
                    </tbody>
                </table>
            </div>
        );
    }

    /**
     * Format the port number of an outlet. Naive implementation assumes that
     * ports are numbered sequentially, but sublcasses can override this to show
     * device-specific formats.
    */
    formatOutletNumber(outletNode, portIndex) {
        if (outletNode?.[portIndex]?.id !== undefined) {
            return outletNode?.[portIndex]?.id;
        }
        return +portIndex + 1;
    }

    renderOutlets(outletNode, baseNode, numOutlets, num, t, prependCb, prependHeaderCb, appendCb) {
        if (this.props.showConfig) {
            return null;
        }

        const self = this, OutputCurrentExists = this.countPortsWithTopic("Current", outletNode) > 0;

        if (!OutputCurrentExists) {
            return null;
        }

        const OutputKWhTotalExists = this.countPortsWithTopic("KWhTotal", outletNode) > 0,
            OutputKWhSubtotalExists = this.countPortsWithTopic("KWhSubtotal", outletNode) > 0,
            OutputPowerWattsExists = this.countPortsWithTopic("PowerWatts", outletNode) > 0,
            OutputPowerVAExists = this.countPortsWithTopic("PowerVA", outletNode) > 0,
            OutputPowerVARExists = this.countPortsWithTopic("PowerVAR", outletNode) > 0,
            OutputPowerFactorExists = this.countPortsWithTopic("PowerFactor", outletNode) > 0,
            OutputPhaseAngleExists = this.countPortsWithTopic("PhaseAngle", outletNode) > 0,
            OutputActualVoltageExists = this.countPortsWithTopic("Voltage", outletNode) > 0,
            OutputLineFrequencyExists = this.countPortsWithTopic("LineFreq", outletNode) > 0,
            OutputResidualCurrentACExists = this.countPortsWithTopic("ResidualCurrentAC", outletNode) > 0,
            OutputResidualCurrentExists = this.countPortsWithTopic("ResidualCurrent", outletNode) > 0,
            OutputActivePowerExists = this.countPortsWithTopic("PowerW", outletNode) > 0,
            OutputApparentPowerExists = this.countPortsWithTopic("ApparentPower", outletNode) > 0,
            OutputCrestFactorExists = this.countPortsWithTopic("CrestFactor", outletNode) > 0,
            OutputActiveEnergyExists = this.countPortsWithTopic("ActiveEnergy", outletNode) > 0;

        // Let's check if there is a grouping of outlets. This would happen over a mapping to a parent
        let outletGroups = groupOutlets(outletNode);

        function renderRow(portIndex, firstCol, lastCol) {
            if (outletNode[portIndex].Current) {
                let className;
                if (lastCol) {
                    className = "groupEnd";
                } else if (firstCol) {
                    className = "groupStart";
                }
                outletRows.push((
                    <tr key={"measure-" + portIndex} className={ className }>
                        { firstCol }
                        <td className="ta-right">{ self.formatOutletNumber(outletNode, portIndex)}</td>
                        <TableCell dataPath={"o/" + portIndex } subAttr="name" readOnly={true} />
                        { prependCb != null && prependCb(portIndex, outletNode[portIndex]) }
                        { OutputKWhTotalExists && (<TableCell dataPath={"o/" + portIndex + "/KWhTotal"} popupName={t('topic_titles.kwhtotal')} chartTitle={t('topic_titles.kwhtotal')} dataRound={2} unit="kWh" /> )}
                        { OutputKWhSubtotalExists && (<TableCell dataPath={"o/" + portIndex + "/KWhSubtotal"} dataRound={2} unit="kWh" popupName={t('topic_titles.kwhsubtotal')} chartTitle={t('topic_titles.kwhsubtotal')} labelPath={"i/" + portIndex + "/OutputRelativeTime"} labelFormatter={function(tsObj) { return new Date(tsObj.v).toLocaleTimeString(); }} /> )}
                        { OutputPowerVAExists && (<TableCell dataPath={"o/" + portIndex + "/PowerVA"} popupName={t('topic_titles.powerva')} chartTitle={t('topic_titles.powerva')} dataRound={2} unit="VA" /> )}
                        { OutputLineFrequencyExists && (<TableCell dataPath={"o/" + portIndex + "/LineFreq"} dataRound={2} unit="Hz" popupName={t('topic_titles.linefrequency')} chartTitle={t('topic_titles.linefrequency')} /> )}
                        { OutputResidualCurrentACExists && (<TableCell dataPath={"o/" + portIndex + "/ResidualCurrentAC"} dataRound={1} format="multiplyBy" unit="mA"  popupName={t('topic_titles.residualcurrentac')} chartTitle={t('topic_titles.residualcurrentac')} /> )}
                        { OutputResidualCurrentExists && (<TableCell dataPath={"o/" + portIndex + "/ResidualCurrent"} dataRound={1} format="multiplyBy" unit="mA"  popupName={t('topic_titles.residualcurrent')} chartTitle={t('topic_titles.residualcurrent')} /> )}
                        { OutputPowerVARExists && (<TableCell dataPath={"o/" + portIndex + "/PowerVAR"}  popupName={t('topic_titles.powervar')} chartTitle={t('topic_titles.powervar')} dataRound={0} unit="VAR" /> )}
                        { OutputPhaseAngleExists && (<TableCell dataPath={"o/" + portIndex + "/PhaseAngle"}  popupName={t('topic_titles.phaseangle')} chartTitle={t('topic_titles.phaseangle')}  dataRound={0} unit="°" /> )}
                        { OutputPowerFactorExists && (<TableCell dataPath={"o/" + portIndex + "/PowerFactor"}  dataRound={2}   unit="" popupName={t('topic_titles.powerfactor')} chartTitle={t('topic_titles.powerfactor')}/> )}
                        { OutputCrestFactorExists && (<TableCell dataPath={"o/" + portIndex + "/CrestFactor"}  dataRound={2}   unit=""  popupName={t('topic_titles.crestfactor')} chartTitle={t('topic_titles.crestfactor')} /> )}
                        { OutputActualVoltageExists && (<TableCell dataPath={"o/" + portIndex + "/Voltage"} dataRound={1} unit="V"  popupName={t('topic_titles.voltage')} chartTitle={t('topic_titles.voltage')}  /> )}
                        { OutputActivePowerExists && (<TableCell dataPath={"o/" + portIndex + "/PowerW"} dataRound={2} unit="W"  popupName={t('topic_titles.powerw')} chartTitle={t('topic_titles.powerw')}  /> )}
                        { OutputApparentPowerExists && (<TableCell dataPath={"o/" + portIndex + "/ApparentPower"} dataRound={2} unit="VA"  popupName={t('topic_titles.apparentpower')} chartTitle={t('topic_titles.apparentpower')}/> )}
                        { OutputActiveEnergyExists && (<TableCell dataPath={"o/" + portIndex + "/ActiveEnergy"} dataRound={1} unit="Wh"/> )}
                        { appendCb != null && appendCb(portIndex, outletNode[portIndex]) }
                        <TableCell dataPath={"o/" + portIndex + "/Current"} dataRound={2} unit="A"  popupName={t('topic_titles.current')} chartTitle={t('topic_titles.current')}/>
                        <ChartCell chartPath={"o/" + portIndex + "/Current"} unit="A"  chartTitle={t('topic_titles.current')} />
                    </tr>
                ));
            }            
        }

        let outletRows = [], outlets = [];
        let groupHeaderTitle = null;

        if (Object.entries(outletGroups).length > 1) {
            groupHeaderTitle = (
                <th>{t("details_output_measures.connected_to")}</th>
            );
            // In this mode we render as the multi-row group description cell in the first column
            for (let inlet in outletGroups) {
                const basePath = "sensor/" + this.props.uuid + "/";
                let headerCell = (
                    <OutletGroupHeader key={"groupcol-" + inlet} renderPlain={false} inletField="Current" dataRound={2} unit="A" basePath={basePath} inletPath={inlet} numOutlets={outletGroups[inlet].length} />
                );
                let i = 0;
                for (let portIndex of outletGroups[inlet]) {
                    renderRow(portIndex, headerCell, ++i == outletGroups[inlet].length);
                    headerCell = null;
                }
            }

        } else {
            for (let portIndex in outletNode) {
                renderRow(portIndex, null);
            }
        }

        if (outletRows.length) {
            outlets = (
                <div className="overview__details__item">
                    <h3>{t('details_output_measures.header')}</h3>
                    <table cellSpacing={0} cellPadding={0}>
                        <thead>
                            <tr>
                                { groupHeaderTitle }
                                <th className="ta-right">#</th>
                                <th>{t('general.name')}</th>
                                { prependHeaderCb != null && prependHeaderCb() }
                                { OutputKWhTotalExists && ( <th className="ta-right">{t('topic_titles.kwhtotal')}</th> )}
                                { OutputKWhSubtotalExists && (<th className="ta-right">{ t('topic_titles.kwhsubtotal') }</th> )}
                                { OutputPowerVAExists && (<th className="ta-right">{t('topic_titles.powerva')}</th> )}
                                { OutputLineFrequencyExists && (<th className="ta-right">{t('topic_titles.linefreq')}</th> )}
                                { OutputResidualCurrentACExists && (<th className="ta-right">{t('topic_titles.residualcurrentac')}</th> )}
                                { OutputResidualCurrentExists && (<th className="ta-right">{t('topic_titles.residualcurrent')}</th> )}
                                { OutputPowerVARExists && (<th className="ta-right">{t('topic_titles.powervar')}</th> )}
                                { OutputPhaseAngleExists && (<th className="ta-right">{t('topic_titles.phaseangle')}</th> )}
                                { OutputPowerFactorExists && (<th className="ta-right">{t('topic_titles.powerfactor')}</th> )}
                                { OutputCrestFactorExists && (<th className="ta-right">{t('topic_titles.crestfactor')}</th> )}
                                { OutputActualVoltageExists && (<th className="ta-right">{t('topic_titles.voltage')}</th> )}
                                { OutputActivePowerExists && (<th className="ta-right">{t('topic_titles.powerw')}</th> )}
                                { OutputApparentPowerExists && (<th className="ta-right">{t('topic_titles.apparentpower')}</th> )}
                                { OutputActiveEnergyExists && (<th className="ta-right">{t('topic_titles.activeenergy')}</th> )}
                                <th className="ta-right">{t('topic_titles.current')}</th>
                                <th className="ta-right">{ t('general.history') }</th>
                            </tr>
                        </thead>
                        <tbody>
                            {outletRows}
                        </tbody>
                    </table>
                </div>
            );
        }
        return outlets;
    }

    renderInlets(inletNode, t, onlyForType) {
        let changeableNames = 0, maxLoadInputs = 0, maximumResettable = 0;
        for (let portIdx in inletNode) {
            if (!Object.prototype.hasOwnProperty.call(inletNode, portIdx)) {
                continue;
            }

            const inlet = inletNode[portIdx];
            if (onlyForType && inlet.type !== onlyForType) {
                continue;
            }
            if (inlet.name !== undefined && !inlet.ro) {
                changeableNames++;
            }
            if (inlet.MaximumLoad?.v) {
                maxLoadInputs++;
            }
            if (inlet.ZeroSubTotalKwh) {
                maximumResettable++;
            }
        }
        if (this.props.showConfig) {
            if (changeableNames === 0 && maxLoadInputs === 0) {
                return null;
            }

            let rows = [];
            for (let portIdx in inletNode) {
                if (Object.prototype.hasOwnProperty.call(inletNode, portIdx) &&
                    inletNode[portIdx].name !== undefined &&
                    !inletNode[portIdx].ro &&
                    (onlyForType && inletNode[portIdx].type === onlyForType)) {
                    const phaseInletPath = onlyForType === 'CB' ? inletNode[portIdx].parent :  `i/${portIdx}`;
                    rows.push((
                        <tr key={"setupInput-" + portIdx}>
                            <td className="ta-right">{ +portIdx }</td>
                            <TableCell size="3" dataPath={ phaseInletPath } subAttr="phase" readOnly={true} />
                            <TableCell size="32" dataPath={ `i/${portIdx}` } subAttr="name" readOnly={false} />
                            { inletNode[portIdx].MaximumLoad?.v && (
                                <TableCell dataPath={ `i/${portIdx}/MaximumLoad` } readOnly={true} />
                            )}
                            { inletNode[portIdx].ZeroSubTotalKwh && (
                                <ButtonCell dataPath={`i/${portIdx}/ZeroSubTotalKwh`} cmd="reset" label={t ('general.reset')} />
                            )}
                        </tr>
                    ));
                }
            }

            return (
                <div className="overview__details__item" id="setup-phases">
                    <h3>{ t(`details_setup_inputs.header${onlyForType === 'CB' ? '_cb' : ''}`) }</h3>
                    <table cellSpacing={0} cellPadding={0}>
                        <thead>
                        <tr>
                            <th className="ta-right">#</th>
                            <th>{t('general.phase')}</th>
                            <th>{t('general.name')}</th>
                            { maxLoadInputs > 0 && (
                                <th>{t('details_configuration.max_load')}</th>
                            )}
                            { maximumResettable > 0 && (
                                <th>{t('pdu_details.reset_subtotals')}</th>
                            )}
                        </tr>
                        </thead>
                        <tbody>
                            {rows}
                        </tbody>
                    </table>
                </div>
            );
        }

        let visibleFields = ["NAME", "PHASE"];
        // For all relevant inlets, check if a field is used and then ensure it will be displayed
        function addFields(inlet) {
            // We assume all mapped topics are "interesting"
            for (let topic in mapUnit) {
                if (inlet[topic] !== undefined && visibleFields.indexOf(topic) === -1)  {
                    visibleFields.push(topic);
                }
            }
        }

        // TODO: Put well-defined fields first in the list

        // Find parent inlets and map to their phases
        let parentInletsToPhases = {};
        // Also track if we have any phases to show at all
        let showPhase = false;
        // Find all parent inlets
        for (let portIdx in inletNode) {
            if (!Object.prototype.hasOwnProperty.call(inletNode, portIdx)) {
                continue;
            }
            const inlet = inletNode[portIdx];
            if (inlet.parent === undefined || inlet.parent === null) {
                // It's a parent
                parentInletsToPhases[portIdx] = [];
                addFields(inlet);

                // Now find all child inlets
                for (let childIdx in inletNode) {
                    if (Object.prototype.hasOwnProperty.call(inletNode, childIdx) && inletNode[childIdx].parent === "i/" + portIdx && inletNode[childIdx].phase) {
                        showPhase = true;
                        parentInletsToPhases[portIdx].push(childIdx);
                        addFields(inletNode[childIdx]);
                    }
                }
            }
        }

        // Now lets create a render order; parents followed by their phases
        let orderedIdx = [];
        for (let parentIdx in parentInletsToPhases) {
            orderedIdx.push(parentIdx);
            for (let childIdx of parentInletsToPhases[parentIdx]) {
                orderedIdx.push(childIdx);
            }
        }
        let orderedFields = [];

        // Also sort all fields according to the ordered field list
        for (let fieldName of INLET_FIELD_ORDER) {
            if (fieldName === "NAME" && showPhase) {
                continue;
            }
            if (visibleFields.indexOf(fieldName) !== -1) {
                orderedFields.push(fieldName);
            }
        }

        let rows = [];
        rows.push((
            <tr key="inlet/header">
                <th>{ t('general.parameter') }</th>
                <th colSpan={4} className="ta-right">{ t('general.value')}</th>
            </tr>
        ));

        for (let field of orderedFields) {
            let cols = [];

            // First column is the row "header"
            if (field === "NAME" && !showPhase) {
                cols.push((
                    <td key="title">{ t('general.name') }</td>
                ));
            } else if (field === "PHASE") {
                if (showPhase) {
                    cols.push((
                        <td key="phase">{ t('general.phase') }</td>
                    ));
                } else {
                    continue;
                }
            } else {
                cols.push((
                    <td key={"title-" + field}>{ t('topic_titles.' + field?.toLowerCase()) }</td>
                ));
            }

            for (let idx in orderedIdx) {
                const isParent = parentInletsToPhases[idx] !== undefined;
                const numChildren = isParent ? parentInletsToPhases[idx].length : 0;

                if (field === "NAME" && isParent && !showPhase) {
                    cols.push((
                        <TableCell key={"i/" + idx + "/name"} colSpan={numChildren + 1} dataPath={"i/" + idx} subAttr="name" tdClass="ta-right" readOnly={true} />
                    ));
                } else if (field === "PHASE") {
                    if (showPhase) {
                        if (isParent) {
                            cols.push((
                                <TableCell key={"i/" + idx + "/name"} colSpan={numChildren + 1} dataPath={"i/" + idx} subAttr="name" tdClass="ta-right" readOnly={true} />
                            ));
                        } else {
                            cols.push(<TableCell key={"i/" + idx + "/phase"} dataPath={"i/" + idx} subAttr="name" tdClass="ta-right" readOnly={true} />);
                        }
                    }
                } else {
                    cols.push((
                        <TableCell key={"i/" + idx + "/" + field} dataRound={2} dataPath={"i/" + idx + "/" + field} unit={mapUnit[field] || " "}  popupName={t('topic_titles.' + field?.toLowerCase())} chartTitle={t('topic_titles.' + field?.toLowerCase())}/>
                    ));
                }
            }

            rows.push((
                <tr key={"i/field/" + field}>
                    {cols}
                </tr>
            ));
        }

        let inputmeasures = (
            <div className="overview__details__item" id="inputmeasures">
                <h3>{ t('details_input_measures.header') }</h3>
                <table cellSpacing={0} cellPadding={0}>
                    <tbody>
                        {rows}
                    </tbody>
                </table>
            </div>
        );

        return inputmeasures;
    }

    renderOutletConfig (outletNode, baseNode, numOutlets, num, t) {
        if (!this.props.showConfig || !outletNode) {
            return null;
        }
        let rows = [];
        
        for (let i in outletNode) {
            let readOnly = outletNode[i].ro;
            if (readOnly === undefined) {
                readOnly = false;
            }
            let row = (
                <tr key={"outlet-" + i}>
                    <td className="ta-right">{ this.formatOutletNumber(outletNode, i)}</td>
                    <TableCell dataPath={ "o/" + i  } subAttr="name" size="12" maxLength="32" readOnly={readOnly} />
                </tr>
            );
            rows.push(row);
        }
        return (
            <div className="overview__details__item" id="setup-outlets">
                <h3>{ t('details_setup_outlets.header') }</h3>
                <table cellSpacing={0} cellPadding={0}>
                    <tbody>
                        <tr>
                            <th className="ta-right">#</th>
                            <th>{ t('general.name') }</th>
                        </tr>
                        { rows }
                    </tbody>
                </table>
            </div>
        );
    }

    
    renderResidual (inletNode, baseNode, numResidual, num, t) {
        if (this.props.showConfig || numResidual == 0) {
            return null;
        }
        const ResidualCurrentStatusExists = this.countPortsWithTopic("ResidualCurrentStatus", inletNode) > 0,
            ResidualCurrentExists = this.countPortsWithTopic("ResidualCurrent", inletNode) > 0,
            ResidualCurrentACExists = this.countPortsWithTopic("ResidualCurrentAC", inletNode) > 0,
            ResidualCurrentDCExists = this.countPortsWithTopic("ResidualCurrentDC", inletNode) > 0;
    
        return ( 
            <div className="overview__details__item">
        	    <h3>{t('topic_titles.residualcurrent')}</h3>
        	    <table>
        		    <tbody>
                        <tr>
                            <th>{t('general.name')}</th>
        		            <th className="ta-right">{t('general.value')}</th>
                        </tr>
                        { ResidualCurrentStatusExists && (
                            <tr>
                 	            <td> {t('raritan_details_input_measures.residual_current_status')} </td>
                 	            <TableCell dataPath="i/0/ResidualCurrentStatus" readOnly={true} />
                            </tr>
                        )}
                        { ResidualCurrentExists && (
                            <tr>
                                <td>{t('topic_titles.residualcurrent')}</td>
                                <TableCell dataRound={1} dataPath="i/0/ResidualCurrent"  unit="mA" popupName={t('topic_titles.residualcurrent')} chartTitle={t('topic_titles.residualcurrent')}/>
                            </tr>
                        )}
                        { ResidualCurrentACExists && (
                            <tr>
                                <td>{t('topic_titles.residualcurrentac')}</td>
                                <TableCell dataRound={1} dataPath="i/0/ResidualCurrentAC"  unit="mA" popupName={t('topic_titles.residualcurrentac')} chartTitle={t('topic_titles.residualcurrentac')}/>
                            </tr>
                        )}
                        { ResidualCurrentDCExists && (
                            <tr>
                                <td>{t('topic_titles.residualcurrentdc')}</td>
                                <TableCell dataRound={1} dataPath="i/0/ResidualCurrentDC"  unit="mA" popupName={t('topic_titles.residualcurrentdc')} chartTitle={t('topic_titles.residualcurrentdc')}/>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>);
    }

    renderAfter() {
        return null;
    }

    hashLinkScroll() {
        const { hash } = window.location;
        if (hash !== '') {
            // Push onto callback queue so it runs after the DOM is updated,
            // this is required when navigating from a different page so that
            // the element is rendered on the page before trying to getElementById.
            setTimeout(() => {
                const id = hash.replace('#', '');
                const element = document.getElementById(id);
                if (element) element.scrollIntoView();
            }, 0);
        }
    }

    render () {
        const { t, p } = this.props;
        const num = {};

        if (!p.DS.sensor) {
            return null;
        }

        // console.log("showconfig", this.props.showConfig);
        const baseNode = p.DS.sensor[this.props.uuid], statusNode = baseNode.status,
            outletNode = baseNode.o, 
            inletNode = baseNode.i;


        num[BINARY_IN] = this.countPortsWithTopic("val", baseNode.bi);
        num[BINARY_OUT] = this.countPortsWithTopic("state", baseNode.do);
        num[SWITCHED] = this.countPortsWithTopic("switch", outletNode);
        num[INLET] = this.countPortsWithTopic("Current", inletNode);
        num[RESIDUAL] = this.countPortsWithTopic("ResidualCurrent", inletNode);
        num[OUTLET] = this.countPortsWithTopic("Current", outletNode);
        num[SENSOR] = baseNode.s ? this.countPortsWithTopic("val", baseNode.s) : 0;
        let inputmeasures = [], outlets = [], residualMeasures = [];
        let branchMeasures = [];

        const environs = this.renderEnviron(baseNode.s, baseNode, num[SENSOR], num, t);
        const switches = this.renderSwitches(outletNode, baseNode, num[SWITCHED], num, t);
        const digitalInput = this.renderDigitalInputs(baseNode.bi, baseNode, num[BINARY_IN], num, t);
        const digitalOutput = this.renderDigitalOutputs(baseNode.do, baseNode, num[BINARY_OUT], num, t);

        if (!this.props.showConfig) {
            outlets = this.renderOutlets(outletNode, baseNode, num[OUTLET], num, t);
        } else {
            outlets = this.renderOutletConfig(outletNode, baseNode, num[OUTLET], num, t);
        }

        if (num[INLET] > 0) {
            inputmeasures = this.renderInlets(inletNode, t, 'PHASE');
            if (this.props.showConfig) {
                branchMeasures = this.renderInlets(inletNode, t, 'CB');
            }
        }
        if (num[RESIDUAL] > 0) {
            residualMeasures = this.renderResidual(inletNode, baseNode, num[RESIDUAL], num, t);
        }

        this.hashLinkScroll();
        

        // TODO: status.NumSwitched loop
        // for (var)
        return (
            <div className="overview__details">
                { inputmeasures }
                { this.renderAfter(INLET, num, baseNode, t) }
                { branchMeasures }
                { this.renderAfter(BRANCH, num, baseNode, t) }
                { residualMeasures }
                { outlets }
                { this.renderAfter(OUTLET, num, baseNode, t) }
                { switches }
                { environs }
                { this.renderAfter(SENSOR, num, baseNode, t) }
                { digitalInput }
                { this.renderAfter(BINARY_IN, num, baseNode, t) }
                { digitalOutput }
                { this.renderAfter(BINARY_OUT, num, baseNode, t) }
                { this.props.showConfig && this.renderInfo(statusNode)}
                { this.renderAfter(INFO, num, baseNode, t)}
                        
                <div className="overview__details__item">
                    <h3>{ t('details_setup.header') }</h3>
                    <table cellSpacing={0} cellPadding={0}>
                        <tbody>
                            <tr>
                                <th>{ t('general.parameter') }</th>
                                <th>{ t('general.setting') }</th>
                            </tr>
                            <tr>
                                <td>{ t('general.name') }</td>
                                <TableCell dataPath="" key={`name-${this.props.showConfig}`} subAttr="name" type="text" size="16" optional={true} readOnly={!this.props.showConfig}/>
                            </tr>
                            <tr>
                                <td>{ t('general.vanity_tag') }</td>
                                <TableCell alignRight={true} key={`vanity-${this.props.showConfig}`} tdClass="ta-left" dataPath="config/VanityTag" type="text" size="16" optional={true} readOnly={!this.props.showConfig} />
                            </tr>
                            <tr>
                                <td>{ t('general.location') }</td>
                                <TableCell size="16" key={`location-${this.props.showConfig}`} dataPath="" subAttr="location" type="text" optional={true}  readOnly={!this.props.showConfig}/>
                            </tr>
                        </tbody>
                    </table>
                </div>
                { this.renderAfter(GENERIC, num, baseNode, t) }
            </div>
        );

    }
}

GenericPDUDriver.propTypes = {
    showConfig: PropTypes.bool.isRequired,
    uuid: PropTypes.string.isRequired,
};

export default GenericPDUDriver;
